import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { SiteAsset } from '@/hooks/useSiteAsset';

// Local fallback URLs for critical assets (ensures site never appears broken)
const FALLBACKS: Record<string, string> = {
  logo: '/assets/logo.png',
  hero_image: '/assets/hero-image.jpg',
};

/**
 * Builds the public URL for a site asset from Supabase storage.
 * Includes cache-busting query param based on updated_at timestamp.
 */
function buildAssetUrl(asset: SiteAsset): string {
  const { data } = supabase.storage
    .from(asset.bucket)
    .getPublicUrl(asset.storage_path);

  // Add cache-busting param based on updated_at
  const cacheBuster = new Date(asset.updated_at).getTime();
  return `${data.publicUrl}?v=${cacheBuster}`;
}

interface SiteAssetsContextValue {
  /** Get URL for an asset by key (returns fallback if not loaded yet) */
  getAssetUrl: (key: string) => string | null;
  /** Get full asset record by key */
  getAsset: (key: string) => SiteAsset | null;
  /** Whether initial critical assets are loaded */
  isLoaded: boolean;
  /** Whether assets are currently being fetched */
  isLoading: boolean;
  /** Any error that occurred during fetch */
  error: string | null;
  /** Refetch all assets (call after optimization) */
  refetch: () => Promise<void>;
  /** All loaded assets */
  assets: Map<string, SiteAsset>;
}

const SiteAssetsContext = createContext<SiteAssetsContextValue | undefined>(undefined);

/**
 * Critical assets that should be preloaded on app init.
 * These are required for above-the-fold content and should never cause layout shifts.
 */
const CRITICAL_ASSETS = ['logo', 'hero_image', 'popup_hero'];

interface SiteAssetsProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that preloads critical site assets on app initialization.
 *
 * Features:
 * - Preloads critical assets (logo, hero) to prevent layout shifts
 * - Provides fallback URLs for resilience if Supabase is unavailable
 * - Caches assets in memory for fast access
 * - Exposes refetch for updating after optimization
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <SiteAssetsProvider>
 *   <App />
 * </SiteAssetsProvider>
 *
 * // In any component
 * const { getAssetUrl } = useSiteAssets();
 * const logoUrl = getAssetUrl('logo');
 * ```
 */
export function SiteAssetsProvider({ children }: SiteAssetsProviderProps) {
  const [assets, setAssets] = useState<Map<string, SiteAsset>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await (supabase
        .from('site_assets' as any)
        .select('*') as any);

      if (fetchError) {
        throw fetchError;
      }

      const assetMap = new Map<string, SiteAsset>();
      for (const asset of (data || []) as unknown as SiteAsset[]) {
        assetMap.set(asset.key, asset);
      }

      setAssets(assetMap);
      setIsLoaded(true);
    } catch (err) {
      console.error('Failed to load site assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assets');
      // Still mark as loaded so app can render with fallbacks
      setIsLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch assets on mount
  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const getAssetUrl = useCallback((key: string): string | null => {
    const asset = assets.get(key);

    if (asset) {
      return buildAssetUrl(asset);
    }

    // Return fallback if available
    if (FALLBACKS[key]) {
      return FALLBACKS[key];
    }

    return null;
  }, [assets]);

  const getAsset = useCallback((key: string): SiteAsset | null => {
    return assets.get(key) || null;
  }, [assets]);

  const value = useMemo<SiteAssetsContextValue>(() => ({
    getAssetUrl,
    getAsset,
    isLoaded,
    isLoading,
    error,
    refetch: fetchAssets,
    assets,
  }), [getAssetUrl, getAsset, isLoaded, isLoading, error, fetchAssets, assets]);

  return (
    <SiteAssetsContext.Provider value={value}>
      {children}
    </SiteAssetsContext.Provider>
  );
}

/**
 * Hook to access site assets from the context.
 * Must be used within a SiteAssetsProvider.
 *
 * @example
 * ```tsx
 * const { getAssetUrl, isLoaded } = useSiteAssetsContext();
 *
 * // Get URL with automatic fallback
 * const logoUrl = getAssetUrl('logo');
 *
 * // Check if loaded (for loading states)
 * if (!isLoaded) return <LoadingSpinner />;
 * ```
 */
export function useSiteAssetsContext(): SiteAssetsContextValue {
  const context = useContext(SiteAssetsContext);

  if (!context) {
    throw new Error('useSiteAssetsContext must be used within a SiteAssetsProvider');
  }

  return context;
}

/**
 * Hook to get a single asset URL from context.
 * Convenience wrapper around useSiteAssetsContext.
 *
 * @param key - The asset key (e.g., 'logo', 'hero_image')
 * @returns The asset URL (Supabase or fallback) or null
 *
 * @example
 * ```tsx
 * const logoUrl = useSiteAssetUrl('logo');
 * return <img src={logoUrl || ''} alt="Logo" />;
 * ```
 */
export function useSiteAssetUrl(key: string): string | null {
  const { getAssetUrl } = useSiteAssetsContext();
  return getAssetUrl(key);
}

export default SiteAssetsContext;
